import { ref, computed, onMounted } from "vue";
import { TOUR_STEPS } from "./TourSlider.constants";
import { setTourCompleted } from "vue-app/api/AccountService";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";

const trackerId = window.EDRONE_CONTEXT.trackerId;
const agentId = window.EDRONE_CONTEXT.user.agentId;
const STORAGE_KEY = `${trackerId}-${agentId}-tour-progress`;

const isOpen = ref(false);
const currentStepIndex = ref(0);
const initialized = ref(false);

function loadProgress(): void {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    currentStepIndex.value = parsed.currentStepIndex ?? 0;
  }
}

function saveProgress(): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      currentStepIndex: currentStepIndex.value,
      completed: false,
    })
  );
}

function markCompleted(): void {
  if (window.EDRONE_CONTEXT.oneTimeActions) {
    window.EDRONE_CONTEXT.oneTimeActions.REGISTER_TOUR = true;
  }
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      currentStepIndex: currentStepIndex.value,
      completed: true,
    })
  );
}

function isCompleted(): boolean {
  return window.EDRONE_CONTEXT.oneTimeActions?.REGISTER_TOUR ?? false;
}

function resetTour(): void {
  localStorage.removeItem(STORAGE_KEY);
  currentStepIndex.value = 0;
}

function handleTourOpenEvent(): void {
  const saved = localStorage.getItem(STORAGE_KEY);
  let parsedData: Record<string, unknown> | null = null;

  if (saved) {
    parsedData = JSON.parse(saved);
  }

  if (!parsedData?.startedAt) {
    const startedAt = new Date().toISOString();
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...(parsedData || {}),
        currentStepIndex: currentStepIndex.value,
        completed: false,
        startedAt: startedAt,
      })
    );
    amplitudeSendEvent("tour_opened", {
      step: currentStepIndex.value,
      stepId: TOUR_STEPS[currentStepIndex.value]?.id,
      startedAt: startedAt,
    });
  }
}

function initializeTour(): void {
  if (initialized.value) return;
  initialized.value = true;

  if (!isCompleted()) {
    loadProgress();
    isOpen.value = true;
    handleTourOpenEvent();
  }
}

export function useTourSlider() {
  const currentStep = computed(() => TOUR_STEPS[currentStepIndex.value]);
  const totalSteps = computed(() => TOUR_STEPS.length);
  const isFirstStep = computed(() => currentStepIndex.value === 0);
  const isLastStep = computed(() => currentStepIndex.value === TOUR_STEPS.length - 1);
  const progressPercentage = computed(() => ((currentStepIndex.value + 1) / TOUR_STEPS.length) * 100);

  const shouldShowTour = computed(() => {
    return !isCompleted();
  });

  onMounted(() => {
    initializeTour();
  });

  function openTour(): void {
    loadProgress();
    isOpen.value = true;
    handleTourOpenEvent();
  }

  function nextStep(): void {
    if (currentStepIndex.value < TOUR_STEPS.length - 1) {
      const previousStep = currentStepIndex.value;
      currentStepIndex.value++;
      amplitudeSendEvent("tour_step_next", {
        fromStep: previousStep,
        fromStepId: TOUR_STEPS[previousStep]?.id,
        toStep: currentStepIndex.value,
        toStepId: TOUR_STEPS[currentStepIndex.value]?.id,
      });
      saveProgress();
    }
  }

  function prevStep(): void {
    if (currentStepIndex.value > 0) {
      const previousStep = currentStepIndex.value;
      currentStepIndex.value--;
      amplitudeSendEvent("tour_step_prev", {
        fromStep: previousStep,
        fromStepId: TOUR_STEPS[previousStep]?.id,
        toStep: currentStepIndex.value,
        toStepId: TOUR_STEPS[currentStepIndex.value]?.id,
      });
      saveProgress();
    }
  }

  async function completeTour(): Promise<void> {
    amplitudeSendEvent("tour_completed", {
      totalSteps: TOUR_STEPS.length,
      completedAt: new Date().toISOString(),
    });
    markCompleted();
    isOpen.value = false;
    currentStepIndex.value = 0;

    try {
      await setTourCompleted();
    } catch (error) {
      console.error("Failed to sync tour completion with backend:", error);
    }
  }

  function goToStep(index: number): void {
    if (index >= 0 && index < TOUR_STEPS.length) {
      const previousStep = currentStepIndex.value;
      currentStepIndex.value = index;
      amplitudeSendEvent("tour_step_jumped", {
        fromStep: previousStep,
        fromStepId: TOUR_STEPS[previousStep]?.id,
        toStep: index,
        toStepId: TOUR_STEPS[index]?.id,
      });
      saveProgress();
    }
  }

  function restartTour(): void {
    amplitudeSendEvent("tour_restarted", {});
    resetTour();
    isOpen.value = true;
  }

  return {
    isOpen,
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    progressPercentage,
    shouldShowTour,
    openTour,
    nextStep,
    prevStep,
    completeTour,
    goToStep,
    restartTour,
  };
}
